'use client';

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import VBox from '../../components/layout/VBox';
import { AnimatedBackground } from '../login/bg/AnimatedBackground';
import useWindowSize from '../../lib/useWindowSize';
import Icon from '@/components/Icon';
import { mdiLoading, mdiCheckCircle, mdiAlertCircle, mdiPauseCircle, mdiPlayCircle } from '@mdi/js';
import Botao from '@/components/Botao';

type PageState = 'validating' | 'success' | 'error';

const AuthMagicLinkPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [width, height, mobile] = useWindowSize();
    const [pause, setPause] = useState(false);
    const [state, setState] = useState<PageState>('validating');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const token = searchParams.get('token');

        if (!token) {
            setState('error');
            setErrorMessage('Token não fornecido. Verifique o link recebido por email.');
            return;
        }

        validateMagicLink(token);
    }, [searchParams]);

    const validateMagicLink = async (token: string) => {
        try {
            const response = await axios.get(`/api/auth/magic-link/verify?token=${token}`);

            if (response.data.ok) {
                setState('success');
                window.location.href = '/chat';
            }
        } catch (error: any) {
            setState('error');
            if (axios.isAxiosError(error) && error.response) {
                setErrorMessage(error.response.data.msg || 'Erro ao validar link');
            } else {
                setErrorMessage('Erro ao processar solicitação. Tente novamente mais tarde.');
            }
        }
    };

    return (
        <div
            style={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10rem',
            }}
        >
            {!mobile && (
                <img
                    src="/logo-terachat-branco.png"
                    alt="Logo TeraChat"
                    style={{ width: '25%' }}
                />
            )}
            <div>
                <div
                    style={{
                        minWidth: '400px',
                        width: mobile ? '95%' : '25%',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        borderRadius: '24px',
                        padding: '40px',
                        gap: '32px',
                        position: 'relative',
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        transition: 'all 0.3s ease',
                        background: 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.5) 100%)',
                    }}
                >
                    <VBox style={{ textAlign: 'center', gap: 16 }}>
                        {mobile && (
                            <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                <img
                                    src="/logo-terachat-branco.png"
                                    alt="Logo TeraChat"
                                    style={{ width: '30%' }}
                                />
                            </div>
                        )}

                        {state === 'validating' && (
                            <>
                                <div>
                                    <Icon
                                        path={mdiLoading}
                                        size={3}
                                        spin
                                        color="var(--tc-color-primary)"
                                    />
                                </div>
                                <h2 style={{
                                    fontSize: '28px',
                                    fontWeight: '600',
                                    color: 'var(--tc-color-gray-900)'
                                }}>Validando acesso...</h2>
                                <p style={{
                                    fontSize: '16px',
                                    color: 'var(--tc-color-gray-800)',
                                    lineHeight: '1.5',
                                }}>
                                    Por favor, aguarde enquanto validamos seu link de acesso.
                                </p>
                            </>
                        )}

                        {state === 'success' && (
                            <>
                                <div>
                                    <Icon
                                        path={mdiCheckCircle}
                                        size={3}
                                        color="var(--tc-color-success)"
                                    />
                                </div>
                                <h2 style={{
                                    fontSize: '28px',
                                    fontWeight: '600',
                                    color: 'var(--tc-color-gray-900)'
                                }}>Acesso Autorizado! ✓</h2>
                                <p style={{
                                    fontSize: '16px',
                                    color: 'var(--tc-color-gray-800)',
                                    lineHeight: '1.5',
                                }}>
                                    Você será redirecionado em instantes...
                                </p>
                            </>
                        )}

                        {state === 'error' && (
                            <>
                                <div>
                                    <Icon
                                        path={mdiAlertCircle}
                                        size={3}
                                        color="var(--tc-color-danger)"
                                    />
                                </div>
                                <h2 style={{
                                    fontSize: '28px',
                                    fontWeight: '600',
                                    color: 'var(--tc-color-gray-900)'
                                }}>Link Inválido</h2>
                                <p style={{
                                    fontSize: '16px',
                                    color: 'var(--tc-color-gray-800)',
                                    lineHeight: '1.5',
                                }}>
                                    {errorMessage}
                                </p>
                                <p style={{
                                    fontSize: '14px',
                                    color: 'var(--tc-color-gray-700)',
                                    lineHeight: '1.4',
                                }}>
                                    O link pode ter expirado ou já foi utilizado. Solicite um novo link de acesso.
                                </p>

                                <Botao
                                    onClick={() => navigate('/login?esqueci-senha=true')}
                                    variant="primary"
                                >
                                    Solicitar Novo Link
                                </Botao>

                                <Botao
                                    onClick={() => navigate('/login')}
                                    style={{ width: '100%' }}
                                    variant="secondary"
                                >
                                    Voltar para Login
                                </Botao>
                            </>
                        )}
                    </VBox>
                </div>
            </div>
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
                {!pause && (
                    <Botao
                        variant="transparent"
                        onClick={() => setPause(true)}
                        icon={mdiPauseCircle}
                        tabIndex={0}
                    >
                        Pausar animação
                    </Botao>
                )}
                {pause && (
                    <Botao
                        variant="transparent"
                        onClick={() => setPause(false)}
                        icon={mdiPlayCircle}
                        tabIndex={0}
                    >
                        Continuar animação
                    </Botao>
                )}
            </div>
            <AnimatedBackground width={width} height={height} pause={pause} />
        </div>
    );
};

export default AuthMagicLinkPage;
